import React, { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  Filter,
  Database,
  Star,
  Loader2,
} from 'lucide-react';
import type {
  ExerciseDefinition,
  MovementPattern,
  MuscleGroup,
  ExerciseTag,
} from '../types';
import {
  MOVEMENT_PATTERN_LABELS,
  MUSCLE_GROUP_LABELS,
  EXERCISE_TAG_LABELS,
} from '../types';
import { EXERCISE_DB } from '../data/exercises';
import { useCustomExercisesStore } from '../store/useCustomExercisesStore';

// ── Default values for new exercise form ────
const EMPTY_FORM = {
  name: '',
  movementPattern: 'squat' as MovementPattern,
  primaryMuscle: 'quads' as MuscleGroup,
  secondaryMuscles: [] as MuscleGroup[],
  tag: 'compound' as ExerciseTag,
  defaultSets: 3,
  defaultReps: '8-12',
  defaultRPE: 7,
  defaultRest: 120,
  defaultTempo: '2-1-2-0',
};

// ── Color helpers ───────────────────────────
const TAG_COLORS: Record<ExerciseTag, string> = {
  compound: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
  isolation: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
  accessory: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300',
  warmup: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
};

const PATTERN_COLORS: Record<string, string> = {
  squat: 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400',
  hinge: 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400',
  horizontal_push: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
  vertical_push: 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400',
  horizontal_pull: 'bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400',
  vertical_pull: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
  carry: 'bg-yellow-50 dark:bg-yellow-950/40 text-yellow-600 dark:text-yellow-400',
  core: 'bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-400',
  isolation_upper: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
  isolation_lower: 'bg-fuchsia-50 dark:bg-fuchsia-950/40 text-fuchsia-600 dark:text-fuchsia-400',
};

export default function ExerciseDatabase() {
  const { customExercises, addExercise, updateExercise, deleteExercise, loading, error } = useCustomExercisesStore();
  const [search, setSearch] = useState('');
  const [filterPattern, setFilterPattern] = useState<MovementPattern | ''>('');
  const [filterMuscle, setFilterMuscle] = useState<MuscleGroup | ''>('');
  const [filterTag, setFilterTag] = useState<ExerciseTag | ''>('');
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showFilters, setShowFilters] = useState(false);

  // ── Merged data ───────────────────────────
  const allExercises = useMemo(() => {
    const builtIn = EXERCISE_DB.map((e) => ({ ...e, _custom: false }));
    const custom = customExercises.map((e) => ({ ...e, _custom: true }));
    return [...builtIn, ...custom];
  }, [customExercises]);

  // ── Filtering ─────────────────────────────
  const filtered = useMemo(() => {
    let res = allExercises;
    if (showCustomOnly) res = res.filter((e) => e._custom);
    if (filterPattern) res = res.filter((e) => e.movementPattern === filterPattern);
    if (filterMuscle)
      res = res.filter(
        (e) => e.primaryMuscle === filterMuscle || e.secondaryMuscles.includes(filterMuscle)
      );
    if (filterTag) res = res.filter((e) => e.tag === filterTag);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      res = res.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.movementPattern.replace(/_/g, ' ').includes(q) ||
          e.primaryMuscle.replace(/_/g, ' ').includes(q) ||
          MOVEMENT_PATTERN_LABELS[e.movementPattern].toLowerCase().includes(q) ||
          MUSCLE_GROUP_LABELS[e.primaryMuscle].toLowerCase().includes(q)
      );
    }
    return res;
  }, [allExercises, search, filterPattern, filterMuscle, filterTag, showCustomOnly]);

  // ── Grouped by pattern ────────────────────
  const grouped = useMemo(() => {
    const groups = new Map<MovementPattern, typeof filtered>();
    for (const e of filtered) {
      const arr = groups.get(e.movementPattern) || [];
      arr.push(e);
      groups.set(e.movementPattern, arr);
    }
    return groups;
  }, [filtered]);

  // ── Stats ─────────────────────────────────
  const stats = useMemo(() => {
    return {
      total: allExercises.length,
      builtIn: EXERCISE_DB.length,
      custom: customExercises.length,
      patterns: new Set(allExercises.map((e) => e.movementPattern)).size,
    };
  }, [allExercises, customExercises]);

  const activeFilters =
    (filterPattern ? 1 : 0) + (filterMuscle ? 1 : 0) + (filterTag ? 1 : 0) + (showCustomOnly ? 1 : 0);

  // ── Form handlers ─────────────────────────
  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editingId) {
      updateExercise(editingId, { ...form });
      setEditingId(null);
    } else {
      addExercise({ ...form });
    }
    resetForm();
  };

  const startEdit = (ex: ExerciseDefinition) => {
    setForm({
      name: ex.name,
      movementPattern: ex.movementPattern,
      primaryMuscle: ex.primaryMuscle,
      secondaryMuscles: [...ex.secondaryMuscles],
      tag: ex.tag,
      defaultSets: ex.defaultSets,
      defaultReps: ex.defaultReps,
      defaultRPE: ex.defaultRPE,
      defaultRest: ex.defaultRest,
      defaultTempo: ex.defaultTempo,
    });
    setEditingId(ex.id);
    setShowAddForm(true);
  };

  const handleSecondaryToggle = (m: MuscleGroup) => {
    setForm((prev) => ({
      ...prev,
      secondaryMuscles: prev.secondaryMuscles.includes(m)
        ? prev.secondaryMuscles.filter((x) => x !== m)
        : [...prev.secondaryMuscles, m],
    }));
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      {/* ── Header ─────────────────────── */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
          <Database size={22} className="text-brand-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Baza ćwiczeń</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {stats.builtIn} wbudowanych + {stats.custom} własnych = {stats.total} ćwiczeń
            &nbsp;·&nbsp;{stats.patterns} wzorców ruchu
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
          }}
          className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
        >
          <Plus size={16} />
          Dodaj ćwiczenie
        </button>
      </div>

      {/* ── Add / Edit form ────────────── */}
      {showAddForm && (
        <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
            {editingId ? 'Edytuj ćwiczenie' : 'Nowe ćwiczenie'}
          </h3>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Nazwa ćwiczenia <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="np. Wiosłowanie sztangą nachwytem"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {/* Movement pattern */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Wzorzec ruchu</label>
                <select
                  value={form.movementPattern}
                  onChange={(e) => setForm({ ...form, movementPattern: e.target.value as MovementPattern })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(MOVEMENT_PATTERN_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Primary muscle */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Gł. partia mięśniowa</label>
                <select
                  value={form.primaryMuscle}
                  onChange={(e) => setForm({ ...form, primaryMuscle: e.target.value as MuscleGroup })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(MUSCLE_GROUP_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Tag */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Typ ćwiczenia</label>
                <select
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value as ExerciseTag })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(EXERCISE_TAG_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Secondary muscles */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">
                Dodatkowe partie mięśniowe
              </label>
              <div className="flex flex-wrap gap-1.5">
                {(Object.entries(MUSCLE_GROUP_LABELS) as [MuscleGroup, string][]).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => handleSecondaryToggle(k)}
                    className={`px-2 py-1 rounded text-[11px] font-medium transition-all ${
                      form.secondaryMuscles.includes(k)
                        ? 'bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 ring-1 ring-brand-400'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Defaults row */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Serie</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={form.defaultSets}
                  onChange={(e) => setForm({ ...form, defaultSets: parseInt(e.target.value) || 3 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Powtórzenia</label>
                <input
                  type="text"
                  value={form.defaultReps}
                  onChange={(e) => setForm({ ...form, defaultReps: e.target.value })}
                  placeholder="np. 8-12"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">RPE</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  step={0.5}
                  value={form.defaultRPE}
                  onChange={(e) => setForm({ ...form, defaultRPE: parseFloat(e.target.value) || 7 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Odpoczynek (s)</label>
                <input
                  type="number"
                  min={0}
                  max={600}
                  step={15}
                  value={form.defaultRest}
                  onChange={(e) => setForm({ ...form, defaultRest: parseInt(e.target.value) || 90 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Tempo</label>
                <input
                  type="text"
                  value={form.defaultTempo}
                  onChange={(e) => setForm({ ...form, defaultTempo: e.target.value })}
                  placeholder="np. 3-1-2-0"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={resetForm}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                Anuluj
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim()}
                className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Check size={16} />
                {editingId ? 'Zapisz zmiany' : 'Dodaj do bazy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Search + Filters ───────────── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj ćwiczeń po nazwie, wzorcu, mięśniu..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
            activeFilters > 0
              ? 'border-brand-400 bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
              : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-500'
          }`}
        >
          <Filter size={16} />
          Filtry{activeFilters > 0 ? ` (${activeFilters})` : ''}
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* ── Filter panel ───────────────── */}
      {showFilters && (
        <div className="mb-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Pattern filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Wzorzec ruchu</label>
              <select
                value={filterPattern}
                onChange={(e) => setFilterPattern(e.target.value as MovementPattern | '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                <option value="">Wszystkie</option>
                {Object.entries(MOVEMENT_PATTERN_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Muscle filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Partia mięśniowa</label>
              <select
                value={filterMuscle}
                onChange={(e) => setFilterMuscle(e.target.value as MuscleGroup | '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                <option value="">Wszystkie</option>
                {Object.entries(MUSCLE_GROUP_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Tag filter */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Typ</label>
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value as ExerciseTag | '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              >
                <option value="">Wszystkie</option>
                {Object.entries(EXERCISE_TAG_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Custom only */}
            <div className="flex items-end">
              <button
                onClick={() => setShowCustomOnly(!showCustomOnly)}
                className={`w-full px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                  showCustomOnly
                    ? 'border-brand-400 bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:border-brand-300'
                }`}
              >
                <Star size={14} className="inline mr-1" />
                Tylko własne
              </button>
            </div>
          </div>

          {activeFilters > 0 && (
            <button
              onClick={() => {
                setFilterPattern('');
                setFilterMuscle('');
                setFilterTag('');
                setShowCustomOnly(false);
              }}
              className="mt-3 text-xs text-brand-500 hover:text-brand-700 font-medium transition"
            >
              ✕ Wyczyść filtry
            </button>
          )}
        </div>
      )}

      {/* ── Results count ──────────────── */}
      <div className="text-xs text-slate-400 mb-3">
        Wyświetlono {filtered.length} z {allExercises.length} ćwiczeń
      </div>

      {/* ── Exercise list grouped by pattern ── */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 size={32} className="text-brand-600 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ładowanie ćwiczeń...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
            <Dumbbell size={32} className="text-slate-400" />
          </div>
          <h2 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">
            Nie znaleziono ćwiczeń
          </h2>
          <p className="text-sm text-slate-400">
            Spróbuj zmienić kryteria wyszukiwania lub dodaj nowe ćwiczenie
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([pattern, exercises]) => (
            <div key={pattern}>
              {/* Group header */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-md font-semibold ${PATTERN_COLORS[pattern] || ''}`}>
                  {MOVEMENT_PATTERN_LABELS[pattern]}
                </span>
                <span className="text-[10px] text-slate-400">{exercises.length} ćw.</span>
              </div>

              {/* Exercise cards */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                {exercises.map((ex) => (
                  <div
                    key={ex.id}
                    className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition group"
                  >
                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{ex.name}</span>
                        {ex._custom && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-600 dark:text-brand-400 font-bold uppercase flex-shrink-0">
                            Własne
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                        <span>{MUSCLE_GROUP_LABELS[ex.primaryMuscle]}</span>
                        {ex.secondaryMuscles.length > 0 && (
                          <>
                            <span className="text-slate-300 dark:text-slate-600">+</span>
                            <span>{ex.secondaryMuscles.map((m) => MUSCLE_GROUP_LABELS[m]).join(', ')}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Defaults badge */}
                    <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-400 flex-shrink-0">
                      <span>{ex.defaultSets}×{ex.defaultReps}</span>
                      <span>RPE {ex.defaultRPE}</span>
                      <span>{ex.defaultRest}s</span>
                      <span>{ex.defaultTempo}</span>
                    </div>

                    {/* Tag */}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${TAG_COLORS[ex.tag]}`}>
                      {EXERCISE_TAG_LABELS[ex.tag]}
                    </span>

                    {/* Actions (custom only) */}
                    {ex._custom && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => startEdit(ex)}
                          className="p-1 rounded text-slate-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
                          title="Edytuj"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Usunąć "${ex.name}" z bazy?`)) deleteExercise(ex.id);
                          }}
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition"
                          title="Usuń"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
